var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import '../elements/vitesse-icon';
/*
 * <vitesse-artifact> — a file the assistant PRODUCED, shown where it was produced.
 *
 * The archive parks a chart in the conversation's blob store and writes a reference beside the
 * tool result. Until this existed the reference was the end of the road: the bytes were stored,
 * billed and unreachable, which is the same as losing them with a larger invoice. A tool that says
 * "produced chart.png" and shows nothing is a tool that did two thirds of its job.
 *
 * WHY IT FETCHES INSTEAD OF SETTING A `src`. The download route is authed and re-checks the org on
 * every read. An `<img src>` cannot carry a bearer token, and a route that took one in the query
 * string would write a credential into every log, proxy and history entry the URL ever touched. So
 * the bytes come through the AI plane — which already holds the token — and become an object URL
 * in this document. That is also why the loader is injected rather than built here: this element
 * must not know what a token is.
 *
 * ONE OBJECT URL, REVOKED ON THE WAY OUT. An object URL pins its blob for the life of the document
 * unless revoked, so a long transcript that scrolled past fifty charts would hold fifty of them in
 * memory forever.
 */
/*
 * ONE FETCH AND ONE SHAPE PER PICTURE, FOR THE LIFE OF THE PAGE.
 *
 * The transcript is windowed, so a tile that scrolls out of view is DESTROYED and rebuilt when it
 * comes back. Each rebuild re-fetched the bytes and re-made a blob, and for the frames in between
 * the figure had no height — so the content below it moved up, then moved back down when the image
 * decoded. Scrolling past a picture therefore shifted the transcript twice, every time, which is
 * the "keeps jumping up and down and slapping to the top" that was reported. `overflow-anchor` is
 * deliberately off on the log (a 220ms fold animation re-anchors on every frame otherwise), so
 * nothing was absorbing it.
 *
 * Keyed by sha, which is what the blob store is keyed by: the same picture referenced from two
 * turns is one entry. Not revoked on disconnect any more — a revoked URL is exactly what forced the
 * refetch — and the handful of generated images in a session is not a leak worth a cache eviction
 * policy. The page going away releases them.
 *
 * The ASPECT is cached beside the URL and is the half that actually stops the shift: with it the
 * figure reserves its exact height on the first frame of a rebuild, before any bytes are decoded.
 */
const decoded = new Map();
let VitesseArtifact = class VitesseArtifact extends LitElement {
    constructor() {
        super(...arguments);
        /** What it is called, and what a download saves it as. */
        this.filename = '';
        /** The REAL media type, from the record. It decides picture-or-chip, and the server refuses to
         *  serve a scriptable type inline regardless of what this says. */
        this.mediaType = '';
        /** Bytes, so the chip can say how big before anyone asks for it. */
        this.size = 0;
        /** Content address — the cache key, and the only stable identity a tile has across a rebuild. */
        this.sha256 = '';
        this.url = '';
        this.failed = false;
        this.loading = false;
        /** width / height of the decoded picture. Reserves the box before the bytes are back. */
        this.aspect = 0;
    }
    static { this.styles = css `
    :host {
      display: block;
      margin-top: 8px;
    }
    figure {
      margin: 0;
      display: inline-block;
      max-width: 100%;
      /* The band is positioned against the picture, so the picture is the box. A zero line-height
         kills the descender gap an inline image leaves under itself, which would otherwise put a
         sliver of page between the band and the bottom edge it is meant to sit on. */
      position: relative;
      line-height: 0;
      overflow: hidden;
      border-radius: 8px;
    }
    img {
      display: block;
      max-width: 100%;
      /* Bounded so one tall chart cannot push the rest of the turn off the screen. The real size
         is one click away; what belongs in the thread is a look at it. */
      max-height: 420px;
      border-radius: 8px;
      border: 1px solid var(--border-color);
      background: var(--surface-1, #fff);
    }
    /*
     * WHAT YOU CAN DO WITH IT, ON THE PICTURE — a band across the bottom edge, on hover.
     *
     * Two actions and no more: see it properly, and keep it. Anything else about a generated image
     * is a question for the assistant that made it, not a control.
     *
     * Hidden until asked for, because a transcript is mostly read and a permanent bar over every
     * picture is chrome on top of the thing it is about. It comes back on keyboard focus as well as
     * on hover, or the two controls exist only for a mouse.
     */
    .bar {
      position: absolute;
      inset: auto 0 0 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
      padding: 7px 8px;
      line-height: 1;
      background: linear-gradient(to top, rgb(0 0 0 / 62%), rgb(0 0 0 / 0%));
      opacity: 0;
      transition: opacity 0.14s ease;
    }
    figure:hover .bar,
    .bar:focus-within {
      opacity: 1;
    }
    .bar button,
    .bar a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      border: 0;
      border-radius: 7px;
      background: rgb(255 255 255 / 14%);
      color: #fff;
      cursor: pointer;
      text-decoration: none;
      transition:
        background 0.12s ease,
        transform 0.12s ease;
    }
    .bar button:hover,
    .bar a:hover {
      background: rgb(255 255 255 / 28%);
      transform: scale(1.07);
    }
    @media (prefers-reduced-motion: reduce) {
      .bar,
      .bar button,
      .bar a {
        transition: none;
      }
    }
    /*
     * FULL SIZE, over everything. A dialog rather than a new tab: a blob URL opened in a tab is a
     * page with no way back to the thread, and on a rail this narrow the picture is the point.
     */
    dialog::backdrop {
      background: rgb(0 0 0 / 72%);
    }
    dialog {
      position: relative;
      border: 0;
      padding: 0;
      background: none;
      max-width: 92vw;
      max-height: 92vh;
    }
    /*
     * THE WAY OUT, top-right of the picture.
     *
     * Escape and a click on the backdrop both already close it, but neither is visible: on a
     * full-bleed image the dialog reads as a place with no exit. Positioned over the picture rather
     * than beside it because the dialog is sized to the image, so there is no beside.
     */
    .shut {
      position: absolute;
      top: 8px;
      right: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border: 0;
      border-radius: 8px;
      cursor: pointer;
      color: #fff;
      background: rgb(0 0 0 / 55%);
      backdrop-filter: blur(6px);
    }
    .shut:hover {
      background: rgb(0 0 0 / 78%);
    }
    dialog img {
      max-width: 92vw;
      max-height: 92vh;
      border: 0;
    }
    figcaption,
    .chip {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--text-secondary);
    }
    figcaption {
      margin-top: 4px;
    }
    .chip {
      border: 1px solid var(--border-color);
      border-radius: 8px;
      padding: 8px 10px;
      color: var(--text-primary);
      text-decoration: none;
      max-width: 100%;
      background: none;
      font: inherit;
      font-size: 12px;
      text-align: left;
      cursor: pointer;
    }
    .chip:disabled {
      cursor: default;
    }
    .chip:hover:enabled {
      border-color: var(--blue);
      color: var(--blue);
    }
    .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .meta {
      color: var(--text-secondary);
      flex: none;
    }
  `; }
    /** Bytes, in the unit a person reads. Plain digits — a size in a transcript is read by a person,
     *  but grouping separators differ per locale and this sits inside prose that may not match. */
    get sizeText() {
        if (!this.size)
            return '';
        if (this.size < 1024)
            return `${this.size} B`;
        if (this.size < 1024 * 1024)
            return `${Math.round(this.size / 1024)} KB`;
        return `${(this.size / (1024 * 1024)).toFixed(1)} MB`;
    }
    /** Is this something we should draw rather than offer?
     *
     *  SVG is excluded on purpose and not as an oversight: it is an image by extension and a script
     *  host in practice, and the server refuses to serve it inline for exactly that reason. Drawing
     *  it here would promise something the download route then declines. */
    get isImage() {
        return this.mediaType.startsWith('image/') && this.mediaType !== 'image/svg+xml';
    }
    updated(changed) {
        // Keyed on the loader, which the host rebinds per artifact — a tile reused for a different file
        // must not keep the previous one's picture.
        if (changed.has('load') || changed.has('mediaType'))
            void this.fetchBytes();
    }
    async fetchBytes() {
        this.failed = false;
        if (!this.isImage || !this.load)
            return;
        // Already decoded in this page's life — including a tile the transcript's window destroyed and
        // rebuilt a moment ago. Applied synchronously, so the box has its height on the FIRST frame.
        const had = this.sha256 ? decoded.get(this.sha256) : undefined;
        if (had) {
            this.url = had.url;
            this.aspect = had.aspect;
            return;
        }
        this.url = '';
        this.aspect = 0;
        this.loading = true;
        const bytes = await this.load().catch(() => null);
        this.loading = false;
        if (!bytes) {
            this.failed = true;
            return;
        }
        // Sliced: a view into a larger buffer would blob the WHOLE buffer.
        this.url = URL.createObjectURL(new Blob([bytes.slice().buffer], { type: this.mediaType }));
    }
    /** The picture's own proportions, learned once it decodes and remembered with it. */
    measured(e) {
        const img = e.target;
        if (!img.naturalWidth || !img.naturalHeight)
            return;
        this.aspect = img.naturalWidth / img.naturalHeight;
        if (this.sha256)
            decoded.set(this.sha256, { url: this.url, aspect: this.aspect });
        // The box just grew to the picture's real height. A transcript pinned to its bottom needs to
        // know, or the last image on the thread sits half under the composer until the reader fights
        // the scroll past it.
        this.dispatchEvent(new CustomEvent('artifact-measured', { bubbles: true, composed: true }));
    }
    /**
     * SAVE A FILE THAT IS NOT DRAWN — a workbook, a CSV, a PDF.
     *
     * The chip used to be a label with a download icon on it and nothing behind the icon: the file
     * was produced, parked and listed, and could not be had. A model that wrote an Excel workbook
     * had done the work and the person had no way to open it.
     *
     * Fetched on the click, through the same injected loader a picture uses — this element still
     * never sees a credential — and handed to the browser by a throwaway anchor with `download`,
     * which is what makes it SAVE rather than navigate.
     *
     * AS OCTET-STREAM, whatever the file is. A run can write `.html` or `.svg`, and an object URL made
     * with its real type would render as a page of THIS origin to anyone who opened the link instead
     * of saving it — script and all. Bytes typed as opaque are only ever saved. (A picture keeps its
     * real type; it is drawn, and SVG is never drawn — see `isImage`.)
     *
     * Not kept: a file is rarely saved twice, so the URL is released once the save has started, rather
     * than pinning the bytes for the life of the page the way a drawn picture is.
     */
    async download() {
        if (!this.load || this.loading)
            return;
        this.failed = false;
        this.loading = true;
        const bytes = await this.load().catch(() => null);
        this.loading = false;
        if (!bytes) {
            this.failed = true;
            return;
        }
        // Sliced: a view into a larger buffer would blob the WHOLE buffer.
        const url = URL.createObjectURL(new Blob([bytes.slice().buffer], { type: 'application/octet-stream' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = this.filename || t('ai.artifact.alt');
        this.renderRoot.appendChild(a);
        a.click();
        a.remove();
        // Long enough for the browser to have taken the bytes; the save itself does not need the URL.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    /** Open the full-size view. A dialog, so Escape closes it and the thread is still behind it. */
    expand() {
        this.renderRoot.querySelector('dialog')?.showModal();
    }
    render() {
        if (this.isImage && this.url) {
            const name = this.filename || t('ai.artifact.alt');
            /* The box is the picture's shape BEFORE the picture is in it. Without this the figure has no
               height until the bytes decode, and everything below it moves twice — once up, once back. */
            return html `<figure style=${this.aspect ? `aspect-ratio:${this.aspect}` : ''}>
        <img src=${this.url} alt=${name} @load=${(e) => this.measured(e)} />
        <div class="bar">
          <button title=${t('ai.artifact.expand')} aria-label=${t('ai.artifact.expand')} @click=${() => this.expand()}>
            <vitesse-icon name="maximize" size="15"></vitesse-icon>
          </button>
          <!-- An ANCHOR, not a click handler: the download attribute is what makes the browser save
               it under a name instead of navigating to a blob, and it is the control people already
               know how to right-click. -->
          <a title=${t('ai.artifact.download')} aria-label=${t('ai.artifact.download')} href=${this.url} download=${name}>
            <vitesse-icon name="download" size="15"></vitesse-icon>
          </a>
        </div>
        <dialog @click=${(e) => e.target.close()}>
          <img src=${this.url} alt=${name} />
          <button class="shut" title=${t('ai.artifact.close')} aria-label=${t('ai.artifact.close')}>
            <vitesse-icon name="x" size="16"></vitesse-icon>
          </button>
        </dialog>
        <figcaption>
          <span class="name">${this.filename}</span>
          ${this.sizeText ? html `<span class="meta">· ${this.sizeText}</span>` : nothing}
        </figcaption>
      </figure>`;
        }
        /*
         * THE CHIP, which is not only the fallback.
         *
         * A CSV, a PDF, an archive — none of those is something to draw, and offering them as a named
         * thing with its size is the right rendering rather than a degraded one. It is also what an
         * image that failed to load becomes, with a word saying so: a broken picture icon tells a
         * reader nothing about whether the file exists.
         */
        const icon = this.failed ? 'triangle-alert' : this.loading ? 'loader' : 'download';
        const label = this.failed ? t('ai.artifact.failed') : this.sizeText;
        const name = this.filename || t('ai.artifact.alt');
        /* A BUTTON: the icon on it says "download", so clicking it downloads. Disabled only on a surface
           that gave no loader — there is nothing to fetch with — and a failed attempt stays clickable,
           because clicking again is the retry. */
        return html `<button
      class="chip"
      ?disabled=${!this.load}
      title=${t('ai.artifact.download')}
      aria-label=${`${t('ai.artifact.download')} ${name}`}
      @click=${() => this.download()}
    >
      <vitesse-icon name=${icon} size="14"></vitesse-icon>
      <span class="name">${name}</span>
      ${label ? html `<span class="meta">· ${label}</span>` : nothing}
    </button>`;
    }
};
__decorate([
    property({ type: String })
], VitesseArtifact.prototype, "filename", void 0);
__decorate([
    property({ attribute: 'media-type', type: String })
], VitesseArtifact.prototype, "mediaType", void 0);
__decorate([
    property({ type: Number })
], VitesseArtifact.prototype, "size", void 0);
__decorate([
    property({ type: String })
], VitesseArtifact.prototype, "sha256", void 0);
__decorate([
    property({ attribute: false })
], VitesseArtifact.prototype, "load", void 0);
__decorate([
    state()
], VitesseArtifact.prototype, "url", void 0);
__decorate([
    state()
], VitesseArtifact.prototype, "failed", void 0);
__decorate([
    state()
], VitesseArtifact.prototype, "loading", void 0);
__decorate([
    state()
], VitesseArtifact.prototype, "aspect", void 0);
VitesseArtifact = __decorate([
    customElement('vitesse-artifact')
], VitesseArtifact);
export { VitesseArtifact };
//# sourceMappingURL=artifact-view.js.map